/**
 * Sentry error/telemetry wiring for the frontend (#499).
 *
 * Completely gated on `VITE_SENTRY_DSN` being set at build time: when it is
 * absent, `initSentry()` is a no-op and nothing from `@sentry/react` runs.
 * This mirrors the backend's `config/sentry.py` (guarded on `SENTRY_DSN`).
 *
 * `environment` uses `VITE_TENANT_ID` (set per-tenant in Vercel, see
 * docs/sentry-setup.md) rather than the tenant config the app loads from the
 * API, because that config resolves asynchronously after the app — and
 * Sentry — must already be initialized. If `VITE_TENANT_ID` isn't set, this
 * falls back to `import.meta.env.MODE`.
 *
 * `release` is computed at build time in vite.config.ts (`__SENTRY_RELEASE__`,
 * mirroring the GIT_SHA/VERCEL_GIT_COMMIT_SHA approach from #539/#579) and
 * injected as a global constant.
 *
 * Performance tracing and Session Replay are on by default at trial-sized
 * sampling (100%) and dialable per tenant without a code change:
 * - `VITE_SENTRY_TRACES_SAMPLE_RATE` (default 1.0)
 * - `VITE_SENTRY_REPLAY_SAMPLE_RATE` (default 1.0; error replays always 1.0)
 * - `VITE_SENTRY_REPLAY_MASK` — replays are MASKED unless this is exactly
 *   'false'. Masked is the safe default: customer tenants (gulfair, …) must
 *   never ship schedule text into replays without an explicit decision;
 *   demo/hia set 'false' so our own sessions are fully visible.
 */
import * as Sentry from '@sentry/react';

declare const __SENTRY_RELEASE__: string;

export function parseSampleRate(value: unknown, fallback: number): number {
  const parsed = typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback;
  return parsed;
}

export function replayMasked(value: unknown): boolean {
  return value !== 'false';
}

/**
 * Whether Sentry is wired for this build — true only when `VITE_SENTRY_DSN`
 * is set, mirroring `initSentry()`'s gate. Feedback-dependent UI (the
 * FeedbackTab) reads this so it stays hidden in local dev / any unconfigured
 * tenant, where `sendFeedback()` would have no client to post to.
 */
export function isSentryEnabled(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

// Known-noise errors that only ever produce unactionable issues: benign
// browser quirks, aborted in-flight requests from navigation/unmount, and
// extension-injected failures. Anything real (backend 5xx, app bugs) is
// unaffected.
const IGNORE_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'AbortError',
  'Request aborted',
  'canceled',
  'Non-Error promise rejection captured',
];

const DENY_URLS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-web-extension:\/\//,
];

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.user && 'email' in event.user) {
    const { email: _email, ...rest } = event.user;
    event.user = rest;
  }
  return event;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  const environment = import.meta.env.VITE_TENANT_ID || import.meta.env.MODE;
  const release = typeof __SENTRY_RELEASE__ !== 'undefined' ? __SENTRY_RELEASE__ : 'dev';
  const tracesSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0);
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE,
    1.0,
  );
  const mask = replayMasked(import.meta.env.VITE_SENTRY_REPLAY_MASK);

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    beforeSend,
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: mask,
        maskAllInputs: mask,
        blockAllMedia: mask,
      }),
      // Feedback capture wiring only (#641). `autoInject: false` suppresses
      // Sentry's own floating button — the app provides its own Leitmotif
      // trigger (FeedbackTab) and posts messages through the headless
      // `sendFeedback()` API behind the app's Dialog primitive.
      Sentry.feedbackIntegration({ autoInject: false }),
    ],
    tracesSampleRate,
    // Propagate trace headers only to our own backends (same-origin /api plus
    // the Fly/production API hosts). The backend allows the `sentry-trace` /
    // `baggage` request headers in CORS for this.
    tracePropagationTargets: [
      /^\/api\//,
      /\.fly\.dev/,
      /connectiondesigner\.app/,
    ],
    replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,
  });
}
